import React, { useState, useEffect, useContext } from 'react';
import useLocalStorage from 'use-local-storage';
import '../../App.css';
import './user-control.css';
import '../../tools/search-results/search-results.css';
import CSS from 'csstype';
import '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// functions
import { APIPrefix } from '../../utils/functions';

// types
import { FileBrowserPage, NotificationType } from '../../utils/types';

// images
import gearsIcon from '../../icons/gears.gif';
import binDark from '../../icons/bin-dark.256.png';
import binLight from '../../icons/bin-light.256.png';
import checkboxTicked from '../../icons/check-box-ticked.620.png';
import checkboxUnticked from '../../icons/check-box-unticked.620.png';
import unreadIconDark from '../../icons/unread-notification-dark.128.png';
import unreadIconLight from '../../icons/unread-notification-light.128.png';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
  InitiateDMSearchParams,
} from '../../utils/events';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

// class-level constants.
const TABLE_COL: CSS.Properties = {
  padding: '3px 5px 3px 5px',
  whiteSpace: 'normal',
  textAlign: 'left',
};
const TABLE_COL_CENTRED: CSS.Properties = {
  padding: '3px 5px 3px 5px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

// get default user preferences..
const DEFAULT_DARK = window.matchMedia('(prefers-color-scheme: dark)').matches;

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	display a list of appropriate action buttons for this
//	notification type.
//
//	------------------------------------------------------------

function Actions(props: { notification: NotificationType }) {
  const dispatch = useAppDispatch();

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	search the compute API for compute resources to process
  //	these data.
  //
  //	------------------------------------------------------------

  const initiateComputeSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pToNode: string,
    pToSite: string,
  ) => {
    // raise an initiate-compute-search event.
    const params: InitiateComputeSearchParams = {
      node: pToNode,
      site: pToSite,
      serviceType: 'jupyterhub',
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.SEARCH_COMPUTE_TABLE,
      eventType: EventType.INITIATE_COMPUTE_SEARCH,
      parameters: params,
    };
    dispatch(handleEvent({ event: newEvent }));
  }; // initiateComputeSearchHandler

  //	------------------------------------------------------------
  //
  //	search the data-management API for the files being
  //	transferred by this job ID.
  //
  //	------------------------------------------------------------

  const initiateDMSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pJobID: string,
  ) => {
    // run the data-management search.
    const params: InitiateDMSearchParams = {
      jobID: pJobID !== null ? pJobID : '',
      fileType: 'all',
      pageDisplayed: FileBrowserPage.JobDetails,
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
      eventType: EventType.INITIATE_DM_SEARCH,
      parameters: params,
    };
    dispatch(handleEvent({ event: newEvent }));
  }; // initiateDMSearchHandler

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        margin: '0px 0px 0px 0px',
      }}
    >
      {props.notification.notificationType.toUpperCase() === 'DMR' ? (
        <button
          type="button"
          className="menu-button-text"
          title="View data"
          style={{
            flex: '0 0 auto',
            cursor: 'pointer',
            margin: '0px',
            backgroundColor: 'transparent',
          }}
          onClick={(event) =>
            initiateDMSearchHandler(
              /* event = */ event,
              /* pJobID = */ props.notification.additionalInfo.data_management_job_id,
            )
          }
          data-align="R"
        >
          View data
        </button>
      ) : (
        <></>
      )}
      <div style={{ flex: '0 0 10px' }} />
      {props.notification.notificationType.toUpperCase() === 'DMR' ? (
        <button
          type="button"
          className="menu-button-text"
          title="Process data"
          style={{
            flex: '0 0 auto',
            cursor: 'pointer',
            margin: '0px',
            backgroundColor: 'transparent',
          }}
          onClick={(event) =>
            initiateComputeSearchHandler(
              /* event = */ event,
              /* pToNode = */ props.notification.additionalInfo.to_node,
              /* pToSite = */ props.notification.additionalInfo.to_site,
            )
          }
          data-align="R"
        >
          Process data
        </button>
      ) : (
        <></>
      )}
    </div>
  );
} // Actions

//	------------------------------------------------------------
//
//	Constructs a list of columns.
//
//	------------------------------------------------------------

function TableColumns() {
  return (
    <>
      <col style={{ width: '25px' }} key="delete" />
      <col key="unread" />
      <col key="selectCol" />
      <col key="message" />
      <col key="date" />
      <col key="action" />
    </>
  );
} // TableColumns

//	------------------------------------------------------------
//
//	Constructs a table header row from a list of column names.
//
//	------------------------------------------------------------

function TableHeadings(props: {
  sNotifications: NotificationType[];
  sSelectedRows: number[];
  setSelectedRows: any;
  sRenderCount: number;
  setRenderCount: any;
  sSelectAll: boolean;
  setSelectAll: any;
}) {
  //	------------------------------------------------------------
  //
  //	Button handler for check box click events.
  //
  //	------------------------------------------------------------

  const checkChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    // invert the select all check box.
    const selectAll = !props.sSelectAll;
    props.setSelectAll(selectAll);

    // get the list of selected rows from state.
    var selectedRows: number[] = props.sSelectedRows;

    // loop over all the notifications, adding them to the selected-items list
    // if they're not already there.
    if (selectAll === true)
      for (var i: number = 0; i < props.sNotifications.length; i++)
        if (selectedRows.indexOf(props.sNotifications[i].notificationID) === -1)
          selectedRows.push(props.sNotifications[i].notificationID);

    // or clear the selected rows list.
    if (selectAll === false) selectedRows = [];

    // update state.
    props.setSelectedRows(selectedRows);
    props.setRenderCount(props.sRenderCount + 1);
  }; // checkChange

  return (
    <>
      <th style={TABLE_COL} data-hidden="true" key="delete" />
      <th style={TABLE_COL} data-hidden="true" key="unread"></th>
      <th style={TABLE_COL_CENTRED} data-hidden="true" key="selectCol">
        <button
          name="chkNotificationCol"
          type="button"
          title="Select/deselect all"
          className="action-button"
          onClick={checkChange}
          style={{ height: '30px' }}
        >
          <img
            src={props.sSelectAll === true ? checkboxTicked : checkboxUnticked}
            role="decoration"
            alt=""
            width="25"
          />
        </button>
        {/*<input	type = "checkbox"
					onChange = {undefined}
					id = 'selectAll'
					checked = {false} ></input>*/}
      </th>
      <th style={TABLE_COL} data-hidden="true" key="message">
        Message
      </th>
      <th style={TABLE_COL} data-hidden="true" key="date">
        Date
      </th>
      <th style={TABLE_COL} data-hidden="true" key="action">
        Action
      </th>
    </>
  );
} // TableHeadings

//	------------------------------------------------------------
//
//	A HTML component that takes a list of sources and renders
//	them as a list of table rows.
//
//	------------------------------------------------------------

function TableRows(props: {
  sNotifications: NotificationType[];
  sSelectedRows: number[];
  setSelectedRows: any;
  sRenderCount: number;
  setRenderCount: any;
  setSelectAll: any;
}) {
  const dispatch = useAppDispatch();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // which row is currently highlighted?
  const [sHighlightedRow, setHighlightedRow] = useState<number>(-1);

  //	------------------------------------------------------------
  //
  //	delete a single notification.
  //
  //	------------------------------------------------------------

  const deleteNotificationHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pNotificationID: number,
  ) => {
    // call the end point to remove the notification.
    const notificationIDs: number[] = [pNotificationID];

    // raise a delete-notification event.
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.DELETE_NOTIFICATIONS,
      parameters: notificationIDs,
    };
    dispatch(handleEvent({ event: newEvent }));
  }; // deleteNotificationHandler

  //	------------------------------------------------------------
  //
  //	format a date that is in text format.
  //
  //	------------------------------------------------------------

  function formatDate(args: { createdAt: string }) {
    const year: string = args.createdAt.slice(0, 4);
    const month: string = args.createdAt.slice(5, 7);
    const day: string = args.createdAt.slice(8, 10);
    const time: string = args.createdAt.slice(11, 16);
    const formattedDateTime: string = year + '-' + month + '-' + day + ' ' + time;

    // return something.
    return formattedDateTime;
  } // formatDate

  //	------------------------------------------------------------
  //
  //	build the notification text depending upon the notificaiton
  //	type.
  //
  //	------------------------------------------------------------

  function notificationText(args: { notification: NotificationType }) {
    var textValue: string = args.notification.notificationText;

    // DMR: data-management transfer request (READY).
    if (args.notification.notificationType === 'DMR')
      textValue = textValue + ': ' + args.notification.additionalInfo.description;

    // return something.
    return textValue;
  } // notificationText

  //	------------------------------------------------------------
  //
  //	Row is now longer being hovered over.
  //
  //	------------------------------------------------------------

  const onMouseLeave = (event: React.MouseEvent<HTMLTableRowElement>) => {
    setHighlightedRow(-1);
  }; // onMouseLeave

  //	------------------------------------------------------------
  //
  //	Row is being hovered over.
  //
  //	------------------------------------------------------------

  const onMouseOver = (event: React.MouseEvent<HTMLTableRowElement>, index: number) => {
    setHighlightedRow(index);
  }; // onMouseOver

  //	------------------------------------------------------------
  //
  //	Button handler for check box onChange events.
  //
  //	------------------------------------------------------------

  const rowCheckChange = (
    event: React.MouseEvent<HTMLButtonElement>,
    pNotificationID: number,
  ) => {
    // get the list of selected rows from state.
    var selectedRows: number[] = props.sSelectedRows.slice();
    const pos: number = props.sSelectedRows.indexOf(pNotificationID);
    const ticked: boolean = pos === -1;

    // remove the select-all checkbox in the row header.
    if (ticked === false) props.setSelectAll(false);

    // delete the row.
    if (pos > -1 && ticked === false) selectedRows.splice(pos, 1);

    // add the row.
    if (pos === -1 && ticked === true) selectedRows.push(pNotificationID);

    // update state.
    props.setSelectedRows(selectedRows);
    props.setRenderCount(props.sRenderCount + 1);
  }; // rowCheckChange

  return (
    <>
      {props.sNotifications.map((item, index) => (
        <tr
          key={index}
          className="search-results-table-row"
          onMouseOver={(event) => onMouseOver(event, index)}
          onMouseLeave={onMouseLeave}
        >
          <td style={{ ...TABLE_COL_CENTRED, ...{ width: '25px' } }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '34px',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  flex: '0 0 2px',
                  height: '30px',
                  backgroundColor: item.readFlag ? 'transparent' : 'red',
                }}
              />
              <div style={{ flex: '0 0 8px', height: '30px' }} />
              {sHighlightedRow === index ? (
                <button
                  name={'btnDelete_' + index.toString()}
                  type="button"
                  title="Delete notification"
                  style={{
                    display: 'flex',
                    padding: '0px',
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                  onClick={(event) =>
                    deleteNotificationHandler(
                      /* event = */ event,
                      /* pNotificationID = */ item.notificationID,
                    )
                  }
                >
                  <img
                    src={sG_DarkMode ? binDark : binLight}
                    role="decoration"
                    alt=""
                    width="25px"
                    height="25px"
                  />
                </button>
              ) : (
                <div style={{ flex: '0 0 25px' }}></div>
              )}
            </div>
          </td>
          <td style={{ ...TABLE_COL_CENTRED, ...{ paddingTop: '10px', width: '25px' } }}>
            {item.readFlag === false ? (
              <img
                src={sG_DarkMode ? unreadIconDark : unreadIconLight}
                alt="Notification unread"
                width="25px"
              />
            ) : (
              <></>
            )}
          </td>
          <td style={{ ...TABLE_COL_CENTRED, ...{ paddingTop: '10px', width: '25px' } }}>
            <button
              name={'chkNotification_' + index.toString()}
              type="button"
              title="Select/deselect"
              className="action-button"
              onClick={(event) =>
                rowCheckChange(
                  /* event = */ event,
                  /* pNotificationID = */ item.notificationID,
                )
              }
            >
              <img
                src={
                  props.sSelectedRows.indexOf(item.notificationID) === -1
                    ? checkboxUnticked
                    : checkboxTicked
                }
                role="decoration"
                alt=""
                width="25px"
              />
            </button>
          </td>
          <td style={{ ...TABLE_COL, ...{ fontWeight: item.readFlag ? '500' : '800' } }}>
            {notificationText({ notification: item })}
          </td>
          <td style={{ ...TABLE_COL, ...{ width: '180px' } }}>
            {formatDate({ createdAt: item.createdAt })}
          </td>
          <td style={TABLE_COL}>
            {Actions({
              notification: item,
            })}
          </td>
        </tr>
      ))}
    </>
  );
} // TableRows

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function ViewNotifications(props: { notifications: NotificationType[] }) {
  //	-------------------------------------------------
  //
  //	CONSTANTS.
  //
  //	-------------------------------------------------

  // multi-language support
  const { i18n, t } = useTranslation();

  // which rows are currently selected?
  const [sSelectedRows, setSelectedRows] = useState<number[]>([]);
  const [sSelectAll, setSelectAll] = useState<boolean>(false);

  // update render count to refresh the notifications, and check the selected items.
  const [sRenderCount, setRenderCount] = useState<number>(0);

  const [sLoadingComponent, setLoadingComponent] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  //	------------------------------------------------------------
  //
  //	delete one or more notifications.
  //
  //	------------------------------------------------------------

  const deleteNotificationsHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (sSelectedRows.length > 0) {
      // raise a delete-notifications event.
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.DELETE_NOTIFICATIONS,
        parameters: sSelectedRows,
      };
      dispatch(handleEvent({ event: newEvent }));

      // ensure the select-all checkbox is unchecked.
      setSelectAll(false);

      // update state.
      setSelectedRows([]);
    }
  }; // deleteNotificationsHandler

  //	------------------------------------------------------------
  //
  //	Mark the listed notification IDs are READ in the database.
  //
  //	------------------------------------------------------------

  function markNotificationsRead(args: { notificationIDs: number[] }) {
    // call the end point to mark the notifications as read.
    const notificationIDs: number[] = args.notificationIDs;

    // raise a mark-notifications-read event.
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MARK_NOTIFICATIONS_READ,
      parameters: notificationIDs,
    };
    dispatch(handleEvent({ event: newEvent }));
  } // markNotificationsRead

  //	------------------------------------------------------------
  //
  //	Handle changes in the selected rows.
  //
  //	------------------------------------------------------------

  function selectedRowsUpdated(args: { selectedRows: number[] }) {
    // update state.
    setSelectedRows(args.selectedRows);
    setRenderCount(Date.now());
  } // selectedRowsUpdated

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	set event handlers when this component loads, and set
  //	timeout that marks notifications are read.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    // mark the notifications as read after 5 seconds.
    setTimeout(() => {
      markNotificationsRead({
        notificationIDs: props.notifications.map(
          (notification) => notification.notificationID,
        ),
      });
    }, 3000);
    //
  }, []);

  // define a hook that checks if any selected notification IDs no longer exist
  // in the notifications state. if so, we need to remove them.
  useEffect(() => {
    // loop over the selected rows.
    var selectedRows: number[] = sSelectedRows.slice();
    var updated: boolean = false;
    for (var i = selectedRows.length - 1; i >= 0; i--) {
      // try to find this notification ID in the notifications list.
      const pos = props.notifications.findIndex(
        (item) => item.notificationID === selectedRows[i],
      );
      if (pos === -1) {
        selectedRows = selectedRows.splice(pos, 1);
        updated = true;
      }
    }

    // possibly set the select-all checkbox in the row header.
    if (props.notifications.length === selectedRows.length && selectedRows.length > 0)
      setSelectAll(true);

    // set state.
    if (updated === true) setSelectedRows(selectedRows);
  }, [sRenderCount]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      role="region"
      aria-labelledby="view-notifications-label"
      className="gradient-background"
      style={{
        flex: '1 1',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: 'transparent',
        padding: '0px 10px 0px 0px',
      }}
    >
      <div
        className="panel-main"
        style={{
          flexDirection: 'column',
        }}
      >
        <div style={{ height: '0px', flex: '1 1 auto', width: '100%' }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <div style={{ flex: '0 0 10px' }} />

            <div style={{ flex: '1 1', display: 'flex', flexDirection: 'column' }}>
              <div
                id="view-notifications-label"
                className="header-text"
                style={{
                  flex: '0 0 45px',
                  alignItems: 'center',
                  display: 'flex',
                  width: '100%',
                }}
              >
                View notifications
              </div>

              <div style={{ flex: '1 1' }}>
                <div className="search-results-table-holder">
                  <table
                    className={
                      sLoadingComponent === true
                        ? 'search-results-table-hide'
                        : 'search-results-table'
                    }
                    style={{ width: '99%' }}
                  >
                    <colgroup>
                      <TableColumns />
                    </colgroup>
                    <thead>
                      <tr className="search-results-table-row-header">
                        <TableHeadings
                          sNotifications={props.notifications}
                          sSelectedRows={sSelectedRows}
                          setSelectedRows={setSelectedRows}
                          sRenderCount={sRenderCount}
                          setRenderCount={setRenderCount}
                          sSelectAll={sSelectAll}
                          setSelectAll={setSelectAll}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      <TableRows
                        sNotifications={props.notifications}
                        sSelectedRows={sSelectedRows}
                        setSelectedRows={setSelectedRows}
                        sRenderCount={sRenderCount}
                        setRenderCount={setRenderCount}
                        setSelectAll={setSelectAll}
                      />
                    </tbody>
                  </table>

                  <div
                    className={
                      sLoadingComponent === true
                        ? 'search-results-table-loading'
                        : 'search-results-table-loaded'
                    }
                  >
                    <img
                      className="animated-gears"
                      src={gearsIcon}
                      role="decoration"
                      alt=""
                      width="60"
                      height="60"
                    />
                    Loading
                  </div>
                </div>
              </div>
            </div>

            <div style={{ flex: '0 0 10px' }} />
          </div>
        </div>
        <div style={{ flex: '0 0 5px' }} />

        <div
          key={sRenderCount}
          className="view-notification-footer"
          style={{
            flex: '0 0 auto',
            alignItems: 'centre',
            display: sSelectedRows.length > 0 ? 'flex' : 'none',
            flexDirection: 'row',
            width: '100%',
          }}
        >
          <div style={{ flex: '0 0 15px' }} />
          <div
            className="header-text"
            style={{ flex: '0 0 auto', alignItems: 'center', display: 'flex' }}
            data-bold="F"
          >
            {sSelectedRows.length.toString() + ' notification(s) selected:'}
          </div>
          <div style={{ flex: '0 0 50px' }} />
          <div style={{ flex: '0 0 auto' }}>
            <ToolButton
              key={t('Delete')}
              name="deleteNotifications"
              onClick={deleteNotificationsHandler}
              icon={binDark}
              text={t('Delete')}
              type={ToolButtonType.SECONDARY}
            />
          </div>
          <div style={{ flex: '1 1' }} />
          <div style={{ flex: '0 0 15px' }} />
        </div>
      </div>
    </div>
  );
} // ViewNotifications
